import { round } from '@/utils/mathUtils';
import type { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import {
  AlertCircle,
  Calendar,
  Hash,
  Package,
  ShoppingCart,
  TrendingUp,
} from 'lucide-react';
import { useMemo } from 'react';

import { createIDColumn } from '@/components/custom-ui/TableColumns/IDColumn';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip.tsx';
import { useItemStore } from '@/store';
import { DataTable } from '@/UI/TableShadcn';

import type { IStoreIncomingMasterTransaction } from '../../../api/IncomingInvoice.interface';

type Props = {
  Items: IStoreIncomingMasterTransaction[] | undefined;
};

function ItemsTable({ Items }: Props) {
  const { openItemDialog } = useItemStore();

  const columns = useMemo<ColumnDef<IStoreIncomingMasterTransaction>[]>(
    () => [
      createIDColumn<IStoreIncomingMasterTransaction>(),
      {
        accessorKey: 'Name',
        header: 'الصنف',
        cell: (row) => {
          return (
            <div className="flex h-12 w-full items-center justify-start">
              <Button
                variant="link"
                size="sm"
                className="group relative overflow-hidden text-left font-medium text-slate-700 transition-all duration-300 hover:text-blue-600 dark:text-slate-300 dark:hover:text-blue-400"
                onClick={() => {
                  openItemDialog(row.row.original.Item_ID);
                }}
              >
                <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-blue-500/10 to-indigo-500/10 transition-transform duration-300 group-hover:translate-x-0"></div>
                <span className="relative line-clamp-1">
                  {row.row.original.Name}
                </span>
              </Button>
            </div>
          );
        },
      },
      {
        accessorKey: 'Price',
        header: 'الجمهور',
        cell: (row) => {
          const price = round(row?.row?.original?.UnitPrice || 0, 2);
          return (
            <div className="flex items-center justify-center">
              <div className="rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 px-3 py-2 dark:from-emerald-950/30 dark:to-teal-950/30">
                <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                  {price || '-'}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'Qty',
        header: 'الكمية',
        cell: (row) => {
          const qty = round(row?.row?.original?.Qty || 0, 0);
          return (
            <div className="flex items-center justify-center">
              <div className="rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 px-3 py-2 dark:from-blue-950/30 dark:to-indigo-950/30">
                <span className="font-semibold text-blue-700 dark:text-blue-300">
                  {qty || '-'}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'Discount',
        header: 'خصم',
        cell: (row) => {
          const discount = row?.row?.original?.Discount;
          const discountValue = round(discount || 0, 2);

          return (
            <div className="flex w-full justify-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <div className="rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 px-3 py-2 dark:from-amber-950/30 dark:to-orange-950/30">
                      <Badge
                        variant="outline"
                        className="border-amber-200 bg-amber-100 text-amber-700 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300"
                      >
                        {discount || '-'}
                      </Badge>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-1">
                      <p className="font-medium">قيمة الخصم</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {discountValue || '-'}
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        },
      },
      {
        accessorKey: 'TrDate',
        header: 'الباتش',
        cell: (row) => {
          const bno = row?.row?.original?.BNO;
          const expireDate = dayjs(
            new Date(row.row.original.ExpireDate),
          ).format('DD/MM/YYYY');
          const isExpired = dayjs(
            new Date(row.row.original.ExpireDate),
          ).isBefore(dayjs());

          return (
            <div className="flex flex-col items-center justify-center gap-2">
              {/* Batch Number */}
              <div className="flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 px-2 py-1 dark:from-emerald-950/30 dark:to-teal-950/30">
                <Hash className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                  {bno || '-'}
                </span>
              </div>

              {/* Expiry Date */}
              <div
                className={`flex items-center gap-1.5 rounded-lg px-2 py-1 ${
                  isExpired
                    ? 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-950/30 dark:to-rose-950/30'
                    : 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30'
                }`}
              >
                <Calendar
                  className={`h-3 w-3 ${
                    isExpired
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-green-600 dark:text-green-400'
                  }`}
                />
                <span
                  className={`text-xs font-medium ${
                    isExpired
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-green-700 dark:text-green-300'
                  }`}
                >
                  {expireDate}
                </span>
                {isExpired && (
                  <AlertCircle className="h-3 w-3 text-red-600 dark:text-red-400" />
                )}
              </div>
            </div>
          );
        },
      },
    ],
    [openItemDialog],
  );

  return (
    <Card className="border-0 bg-gradient-to-br from-white/80 to-slate-50/80 shadow-lg backdrop-blur-sm dark:from-slate-900/80 dark:to-slate-800/80">
      <CardHeader className="border-b border-slate-200/50 bg-slate-50/30 dark:border-slate-700/50 dark:bg-slate-800/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 shadow-md">
              <ShoppingCart className="h-4 w-4 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                أصناف الفاتورة
              </CardTitle>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {Items?.length || 0} صنف في هذه الفاتورة
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-700 dark:bg-purple-950/30 dark:text-purple-300"
            >
              <Package className="mr-1 h-3 w-3" />
              {Items?.length || 0} صنف
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {Items && Items.length > 0 ? (
          <div className="p-4">
            <DataTable
              columns={columns}
              data={Items}
              visibleColumns={[]}
              isPaginated={false}
              isTopToolbar={false}
              heightTable="120vh"
            />
          </div>
        ) : (
          <div className="flex h-[400px] flex-col items-center justify-center p-8">
            <div className="relative mb-6">
              {/* Empty state icon container */}
              <div className="relative flex h-20 w-20 items-center justify-center">
                {/* Background glow */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-slate-400 to-slate-600 opacity-20 blur-xl"></div>

                {/* Main icon */}
                <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-500 to-slate-700 shadow-lg">
                  <Package className="h-8 w-8 text-white" />
                </div>

                {/* Floating elements */}
                <div className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 shadow-md">
                  <ShoppingCart className="h-3 w-3 text-white" />
                </div>
                <div className="absolute -bottom-2 -left-2 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 shadow-md">
                  <TrendingUp className="h-3 w-3 text-white" />
                </div>
              </div>
            </div>

            <div className="text-center">
              <h3 className="mb-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
                لا يوجد أصناف
              </h3>
              <p className="mb-4 max-w-md text-sm text-slate-600 dark:text-slate-400">
                لم يتم العثور على أي أصناف في هذه الفاتورة. يمكنك إضافة أصناف
                جديدة أو التحقق من البيانات.
              </p>

              {/* Action badges */}
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
                <Badge
                  variant="outline"
                  className="border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-300"
                >
                  <Package className="mr-1 h-3 w-3" />
                  إضافة أصناف
                </Badge>
                <Badge
                  variant="outline"
                  className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
                >
                  <TrendingUp className="mr-1 h-3 w-3" />
                  تحديث البيانات
                </Badge>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ItemsTable;
